// External libraries
import { isObject } from '@checkout/react-components';
import { isString } from '@checkout/react-components';

// Internal modules
import { $logger } from '../logger';
import { getGeoRegion, getGlobals, getLocale } from '../globals';

const getPropOrDefault = ({ config, featureName, propNames = [] }) => {
  const featureConfig = config[featureName];

  if (!isObject(featureConfig)) {
    $logger.warn(`feature_${featureName}_not_found_on_config`);
    return null;
  }

  const keys = [
    ...(Array.isArray(propNames) ? propNames : [propNames]),
    'DEFAULT',
  ];
  const featureKey = keys.find(key => featureConfig.hasOwnProperty(key));
  const featureValue = featureConfig[featureKey];

  $logger.info('client_feature_resolution', {
    featureKey,
    featureName,
    featureValue,
  });

  return featureValue;
};

const getCountryConfigValue = (featureName, config) => {
  const { country } = getLocale();
  const geoRegion = getGeoRegion();

  return getPropOrDefault({
    config,
    featureName,
    propNames: [country, geoRegion],
  });
};

const getInternalOverrides = () => {
  const { isInternalRequest } = getGlobals();
  if (!isInternalRequest) {
    return;
  }

  // Remove `?` at the beginning of the search string
  const query = window.location.search.substring(1);

  if (!query.length) {
    return;
  }

  const WHITE_LIST_REGEX = /^xo__/;
  const mapValue = value => {
    switch (value) {
      case 'true':
        return true;
      case 'false':
        return false;
      default:
        return value;
    }
  };

  return query
    .split('&')
    .filter(param => WHITE_LIST_REGEX.test(param))
    .reduce((overrides, param) => {
      const [key, value = true] = param.split('=');
      const feature = decodeURIComponent(key.substring(4));
      const featureValue = decodeURIComponent(value);

      // For booleans we don't want their value as a string
      overrides[feature] = mapValue(featureValue);
      return overrides;
    }, {});
};

const getExperiment = featureName => {
  return getGlobals().experiments[featureName];
};

const getFactors = featureName => {
  const feature = getExperiment(featureName);
  return feature?.factors || null;
};

const getTreatment = featureName => {
  const feature = getExperiment(featureName);
  return feature?.treatmentName || null;
};

const hasExactTreatment = ({ feature, treatment }) => {
  const treatmentName = getTreatment(feature);
  return Boolean(treatmentName) && treatmentName === treatment;
};

const hasTreatment = feature => {
  const treatment = getTreatment(feature);
  return isString(treatment) && treatment.startsWith('Trmt_');
};

export {
  getCountryConfigValue,
  getFactors,
  getInternalOverrides,
  getPropOrDefault,
  hasExactTreatment,
  hasTreatment,
};
